import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { Navigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import TablePagination from './commonComponents/TablePagination'
import {
  fetchParentHolidays,
  fetchParentStudents,
  getHomeworkErrorMessage,
  parseParentHolidaysResponse,
} from './commonComponents/homeworkApi'

const holidayTypeOptions = [
  { label: 'All', value: 'ALL' },
  { label: 'Student', value: 'STUDENT' },
]

const normalizeHolidayType = (holiday) =>
  String(holiday?.holiday_type ?? holiday?.type ?? '')
    .trim()
    .toUpperCase()

const matchesHolidayTypeFilter = (holiday, filter) => {
  if (!filter || filter === 'ALL') return true
  return normalizeHolidayType(holiday) === filter
}

export default function ParentHolidays() {
  const { logout, user } = useAuth()
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const isParentUser = normalizedUserType === 'parents' || normalizedUserType === 'parent'
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [holidayList, setHolidayList] = useState([])
  const [allStudents, setAllStudents] = useState([])
  const [instituteGroups, setInstituteGroups] = useState([])
  const [holidayTypeFilter, setHolidayTypeFilter] = useState('ALL')
  const [studentFilter, setStudentFilter] = useState('')
  const [loadingHolidays, setLoadingHolidays] = useState(false)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const getDayName = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', { weekday: 'long' })
  }

  const mergeStudents = useCallback((incoming = []) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return
    setAllStudents((prev) => {
      const map = new Map(prev.map((student) => [String(student.id), student]))
      incoming.forEach((student) => {
        const id = student?.id ?? student?.student_id
        if (id == null) return
        map.set(String(id), {
          id,
          name: student?.name ?? student?.student_name ?? '',
          admission_number: student?.admission_number ?? '',
          institute_id: student?.institute_id,
        })
      })
      return Array.from(map.values()).sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      )
    })
  }, [])

  useEffect(() => {
    if (!isParentUser) return
    const loadStudents = async () => {
      try {
        const response = await fetchParentStudents()
        const list = Array.isArray(response?.data?.data) ? response.data.data : []
        mergeStudents(list)
      } catch {
        /* students list is optional; holidays API also returns students */
      }
    }
    loadStudents()
  }, [isParentUser, mergeStudents])

  const fetchHolidays = useCallback(async () => {
    try {
      setLoadingHolidays(true)
      const response = await fetchParentHolidays()
      const parsed = parseParentHolidaysResponse(response)

      mergeStudents(parsed.students)
      setInstituteGroups(parsed.institutes)
      setHolidayList(
        parsed.holidays.map((holiday) => ({
          ...holiday,
          day: getDayName(holiday.date),
          type: normalizeHolidayType(holiday),
        })),
      )
    } catch (error) {
      setHolidayList([])
      setInstituteGroups([])
      toast.error(getHomeworkErrorMessage(error) || 'Failed to fetch holidays')
    } finally {
      setLoadingHolidays(false)
    }
  }, [mergeStudents])

  useEffect(() => {
    if (!isParentUser) return
    fetchHolidays()
  }, [isParentUser, fetchHolidays])

  useEffect(() => {
    setCurrentPage(1)
  }, [search, studentFilter, holidayTypeFilter, itemsPerPage])

  const studentOptions = useMemo(
    () =>
      allStudents
        .filter((student) => student.id != null && (student.name || student.student_name))
        .map((student) => [
          String(student.id),
          student.name || student.student_name || `Student ${student.id}`,
        ]),
    [allStudents],
  )

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

  const filteredByControls = useMemo(() => {
    let rows = holidayList

    if (holidayTypeFilter !== 'ALL') {
      rows = rows.filter((holiday) => matchesHolidayTypeFilter(holiday, holidayTypeFilter))
    }

    if (studentFilter) {
      const studentId = String(studentFilter)
      const selectedStudent = allStudents.find((student) => String(student.id) === studentId)

      rows = rows.filter((holiday) => {
        if (Array.isArray(holiday.student_ids) && holiday.student_ids.length > 0) {
          return holiday.student_ids.includes(studentId)
        }
        if (selectedStudent?.institute_id != null) {
          return String(holiday.institute_id) === String(selectedStudent.institute_id)
        }
        return true
      })
    }

    return rows
  }, [holidayList, holidayTypeFilter, studentFilter, allStudents])

  const searchedHolidays = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return filteredByControls

    return filteredByControls.filter((holiday) => {
      const haystack = [
        holiday.title,
        holiday.date,
        holiday.day,
        holiday.type,
        holiday.holiday_type,
        holiday.session,
        formatDate(holiday.date),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [filteredByControls, search])

  const totalPages = Math.ceil(searchedHolidays.length / itemsPerPage) || 1
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage

  const filteredHolidays = useMemo(
    () => searchedHolidays.slice(indexOfFirstItem, indexOfLastItem),
    [searchedHolidays, indexOfFirstItem, indexOfLastItem],
  )

  useEffect(() => {
    if (searchedHolidays.length === 0) return
    const pages = Math.ceil(searchedHolidays.length / itemsPerPage) || 1
    if (currentPage > pages) setCurrentPage(pages)
  }, [searchedHolidays.length, itemsPerPage, currentPage])

  const tableHeaderStyle = {
    fontSize: '13px',
    fontWeight: '600',
    padding: '16px',
    border: 'none',
    borderBottom: '1px solid #D1D3D4',
    textAlign: 'left',
    color: '#111827',
    fontFamily: 'Inter, system-ui, sans-serif',
  }

  const tableCellStyle = {
    fontSize: '14px',
    padding: '16px',
    border: 'none',
    color: '#111827',
    textAlign: 'left',
    fontFamily: 'Inter, system-ui, sans-serif',
  }

  const handleRefresh = () => {
    setSearch('')
    setHolidayTypeFilter('ALL')
    setStudentFilter('')
    setCurrentPage(1)
    fetchHolidays()
  }

  const selectedStudentName = useMemo(() => {
    if (!studentFilter) return ''
    const match = studentOptions.find(([id]) => id === String(studentFilter))
    return match?.[1] || ''
  }, [studentFilter, studentOptions])

  if (!isParentUser) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <>
      <style>
        {`
          .table td {
            user-select: text;
            overflow: visible !important;
          }
          .parent-holidays-header .dropdown-menu {
            z-index: 9999 !important;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '0px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center parent-holidays-header"
              style={{
                background: '#F3F4F6',
                paddingLeft: isMobile ? '16px' : '28px',
                paddingRight: isMobile ? '16px' : '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                position: 'relative',
                zIndex: 20,
                gap: '12px',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError && logo ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Holidays
                </h3>
              </div>

              {!isMobile && (
                <div
                  className="d-flex align-items-center"
                  style={{ flexShrink: 0, position: 'relative', zIndex: 30 }}
                >
                  <ProfileDropdown
                    logout={logout}
                    drop="down"
                    popperConfig={{
                      strategy: 'fixed',
                      modifiers: [
                        { name: 'offset', options: { offset: [0, 8] } },
                        {
                          name: 'preventOverflow',
                          options: { boundary: 'viewport', padding: 8 },
                        },
                      ],
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '100%',
            overflowX: isMobile ? 'hidden' : 'visible',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center mb-4"
            style={{
              gap: '12px',
              paddingRight: isMobile ? '0px' : '0',
              boxSizing: 'border-box',
            }}
          >
            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: isMobile ? '8px 10px' : '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: isMobile ? '120px' : '140px',
                    maxWidth: '100%',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Holidays
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Find holidays by name, date, type, or student
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={loadingHolidays}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: loadingHolidays ? '#9ca3af' : '#223843',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: loadingHolidays ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                      }}
                      title="Refresh"
                    >
                      <BsArrowClockwise size={20} />
                    </button>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Holiday
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsSearch
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search by holiday, date, type..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Search By Holiday Type
                      </label>
                      <Form.Select
                        value={holidayTypeFilter}
                        onChange={(e) => setHolidayTypeFilter(e.target.value)}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                          fontSize: '14px',
                          height: '40px',
                          backgroundColor: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {holidayTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                  {instituteGroups.length > 0 && (
                    <p className="m-0 mt-3 text-start" style={{ fontSize: '12px', color: '#6b7280' }}>
                      {studentFilter && selectedStudentName
                        ? `Showing holidays for ${selectedStudentName}`
                        : `Showing holidays for ${instituteGroups.length} institute${instituteGroups.length > 1 ? 's' : ''}`}
                      {instituteGroups[0]?.session ? ` · Session ${instituteGroups[0].session}` : ''}
                      {holidayTypeFilter !== 'ALL'
                        ? ` · Type: ${holidayTypeOptions.find((o) => o.value === holidayTypeFilter)?.label || holidayTypeFilter}`
                        : ''}
                      {searchedHolidays.length > 0 ? ` · ${searchedHolidays.length} result${searchedHolidays.length === 1 ? '' : 's'}` : ''}
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}
          </div>

          <Card className="table-card" style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: isMobile ? 'hidden' : 'visible', backgroundColor: '#ffffff', position: 'relative', maxWidth: '100%' }}>
            <div className="table-wrapper" style={{ overflowX: 'auto', overflowY: 'visible', position: 'relative', borderRadius: '12px' }}>
              <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'visible', position: 'relative', marginBottom: '0' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th style={tableHeaderStyle}>Holiday Name</Th>
                    <Th style={tableHeaderStyle}>Date</Th>
                    <Th style={tableHeaderStyle}>Day</Th>
                    <Th style={tableHeaderStyle}>Type</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredHolidays.length > 0 ? (
                    filteredHolidays.map((holiday, index) => (
                      <Tr
                        key={`${holiday.id ?? 'holiday'}-${holiday.institute_id ?? ''}-${holiday.date}-${index}`}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <Td style={tableCellStyle}>{holiday.title}</Td>
                        <Td style={tableCellStyle}>{formatDate(holiday.date)}</Td>
                        <Td style={tableCellStyle}>{holiday.day}</Td>
                        <Td style={tableCellStyle}>{holiday.type}</Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4} style={{ ...tableCellStyle, textAlign: 'center', color: '#6b7280' }}>
                        {loadingHolidays ? 'Loading holidays...' : 'No holidays found.'}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          </Card>

          {searchedHolidays.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              totalItems={searchedHolidays.length}
              itemLabel="holidays"
              className="d-flex justify-content-between align-items-center gap-3 mb-3 mt-4 flex-wrap"
            />
          )}
        </div>
      </div>
    </>
  )
}
